<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import FileSelector from '@/components/FileSelector.vue';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { useReviewsStore } from '@/stores/reviewsStore';
import { useGaiaStore } from '@/stores/gaiaStore';
import { useVaultStore } from '@/stores/vaultStore';
import { useAuthStore } from '@/stores/auth';
import {
  createFileReviews,
  getReviewCost,
  OcrProvider,
  type ReviewCostResponse,
  type ReviewCreateUser,
} from '@/api';
import { fetchWholeDirectory, itemsOfKind } from '@/services/vaultListingService';
import { extractErrorDetail } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

// Props
interface Props {
  show: boolean;
  processing?: boolean;
  preUploadedFiles?: File[];
  triggerConfetti?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  processing: false,
  preUploadedFiles: () => [],
  triggerConfetti: undefined,
});

// Emits
const emit = defineEmits<{
  'update:show': [show: boolean];

  'analysis-started': [evaluation: { uid: string }];
}>();

// Stores
const promptCollectionsStore = usePromptCollectionsStore();
const reviewsStore = useReviewsStore();
const gaiaStore = useGaiaStore();
const vaultStore = useVaultStore();
const authStore = useAuthStore();
const message = useGaiaToast();
const router = useRouter();

// Form state
const analysisName = ref<string>('');
const selectedPromptCollection = ref<string>('');
const selectedFileKeys = ref<string[]>([]);
const showValidation = ref<boolean>(false);
const fileSelectorActiveTab = ref<string>('upload');
const uploadedFilesCount = ref<number>(0);
const pendingUploadFilesCount = ref<number>(0);
const analysisCost = ref<ReviewCostResponse | null>(null);
const analysisCostLoading = ref<boolean>(false);
const analysisCostError = ref<string | null>(null);
const analysisCostSignature = ref<string>('');
const costRequestSequence = ref<number>(0);
const isStartingAnalysis = ref<boolean>(false);
const startAnalysisProgress = ref<number>(0);
const startAnalysisStatus = ref<string>('');

// Component refs
const fileSelectorRef = ref<InstanceType<typeof FileSelector>>();

// Computed properties
const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

const processingAnalysis = computed(() => props.processing);

const promptCollectionOptions = computed(() =>
  promptCollectionsStore.collections.map(collection => ({
    label: collection.name,
    value: collection.uid,
  }))
);

// Validation computed properties
const isAnalysisNameValid = computed(() => !showValidation.value || Boolean(analysisName.value));
const isPromptCollectionValid = computed(
  () => !showValidation.value || Boolean(selectedPromptCollection.value)
);

const selectedInputCount = computed(
  () => selectedFileKeys.value.length + pendingUploadFilesCount.value
);

const currentCostSignature = computed(() => {
  if (!selectedPromptCollection.value || selectedInputCount.value <= 0) {
    return '';
  }

  const selectedKeySignature = [...selectedFileKeys.value].sort().join('|');
  return `${selectedPromptCollection.value}:${selectedKeySignature}:${pendingUploadFilesCount.value}`;
});

const isCostReady = computed(
  () =>
    Boolean(analysisCost.value) &&
    !analysisCostLoading.value &&
    !analysisCostError.value &&
    analysisCostSignature.value === currentCostSignature.value
);

const costNoticeType = computed(() => {
  if (analysisCostError.value) return 'error';
  if (analysisCostLoading.value) return 'info';
  if (
    isCostReady.value &&
    remainingCreditsAfterRun.value !== null &&
    remainingCreditsAfterRun.value < 0
  ) {
    return 'warning';
  }
  if (isCostReady.value) return 'success';
  return 'default';
});

const remainingCreditsAfterRun = computed(() => {
  if (!analysisCost.value) return null;
  return analysisCost.value.remaining_org_credits - analysisCost.value.total_cost;
});

const analysisCreditDeficit = computed(() => {
  if (remainingCreditsAfterRun.value === null || remainingCreditsAfterRun.value >= 0) {
    return 0;
  }

  return Math.abs(remainingCreditsAfterRun.value);
});

const isAnalysisOverCreditLimit = computed(
  () => isCostReady.value && analysisCreditDeficit.value > 0
);

const runAnalysisDisabledReason = computed(() => {
  if (!authStore.userPermissions.can_create_reviews)
    return 'You do not have permission to create reviews.';
  if (isStartingAnalysis.value || processingAnalysis.value) return 'Analysis is starting.';
  if (!analysisName.value) return 'Enter an analysis name.';
  if (!selectedPromptCollection.value) return 'Select a prompt collection.';
  if (selectedInputCount.value <= 0) return 'Select at least one file or directory.';
  if (analysisCostLoading.value) return 'Calculating analysis cost.';
  if (analysisCostError.value) return 'Cost could not be calculated.';
  if (!isCostReady.value) return 'Waiting for the current cost estimate.';
  if (isAnalysisOverCreditLimit.value) return 'Not enough credits to run this analysis.';
  return '';
});

const canRunAnalysis = computed(() => runAnalysisDisabledReason.value === '');

// Modal style computed property
const modalStyle = computed(() => {
  switch (fileSelectorActiveTab.value) {
    case 'upload': {
      return 'width: 50vw; max-width: 600px; height: 90vh';
    }
    case 'file-vault': {
      return 'width: 90vw; max-width: 1000px; height: 95vh';
    }
    case 'gaia-sections': {
      return 'width: 90vw; max-width: 1000px; height: 95vh';
    }
    default: {
      return 'width: 90vw; max-width: 1000px; height: 90vh';
    }
  }
});

// Functions
function handleFilesUploaded(fileUids: string[]): void {
  uploadedFilesCount.value = fileUids.length;
}

function handleUploadSelectionChange(count: number): void {
  pendingUploadFilesCount.value = count;
}

function handleModalClose(): void {
  showValidation.value = false;
  uploadedFilesCount.value = 0;
  resetForm();
}

function handleCancel(): void {
  showModal.value = false;
}

function resetForm(): void {
  analysisName.value = '';
  selectedPromptCollection.value = '';
  selectedFileKeys.value = [];
  showValidation.value = false;
  uploadedFilesCount.value = 0;
  pendingUploadFilesCount.value = 0;
  resetCostState();
  isStartingAnalysis.value = false;
  startAnalysisProgress.value = 0;
  startAnalysisStatus.value = '';
}

function resetCostState(): void {
  analysisCost.value = null;
  analysisCostLoading.value = false;
  analysisCostError.value = null;
  analysisCostSignature.value = '';
  costRequestSequence.value++;
}

function formatCredits(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 2,
  }).format(value);
}

function updateStartProgress(percentage: number, status: string): void {
  startAnalysisProgress.value = percentage;
  startAnalysisStatus.value = status;
}

async function fetchAnalysisCost(
  promptCollectionUid: string,
  numFiles: number,
  signature: string,
  requestSequence: number
): Promise<void> {
  try {
    const response = await getReviewCost({
      query: {
        prompt_collection_uid: promptCollectionUid,
        num_files: numFiles,
      },
    });

    if (requestSequence !== costRequestSequence.value || signature !== currentCostSignature.value) {
      return;
    }

    if (!response.data) {
      throw new Error('Failed to calculate review cost');
    }

    analysisCost.value = response.data;
    analysisCostSignature.value = signature;
    analysisCostError.value = null;
  } catch (error) {
    if (requestSequence !== costRequestSequence.value) {
      return;
    }

    logger.error('Failed to calculate review cost:', error);
    analysisCost.value = null;
    analysisCostSignature.value = '';
    analysisCostError.value = extractErrorDetail(
      error,
      'Failed to calculate analysis cost. Please adjust the selection and try again.'
    );
  } finally {
    if (requestSequence === costRequestSequence.value) {
      analysisCostLoading.value = false;
    }
  }
}

const debouncedFetchAnalysisCost = useDebounceFn(fetchAnalysisCost, 400);

async function collectDirectoryFileUids(
  directoryUid: string,
  fileUids: Set<string>,
  visitedDirectoryUids: Set<string>
): Promise<void> {
  if (visitedDirectoryUids.has(directoryUid)) return;
  visitedDirectoryUids.add(directoryUid);

  const directory = await fetchWholeDirectory(directoryUid);

  for (const file of itemsOfKind(directory.items, 'file')) {
    fileUids.add(file.uid);
  }

  await Promise.all(
    itemsOfKind(directory.items, 'directory').map(directory =>
      collectDirectoryFileUids(directory.uid, fileUids, visitedDirectoryUids)
    )
  );
}

async function getSelectedBillableFileCount(
  selectedKeys: string[],
  selectedUploadCount: number
): Promise<number> {
  const fileUids = new Set(
    selectedKeys.filter(key => key.startsWith('file-')).map(key => key.replace('file-', ''))
  );
  const directoryUids = selectedKeys
    .filter(key => key.startsWith('dir-'))
    .map(key => key.replace('dir-', ''));
  const gaiaFileCount = new Set(
    selectedKeys.filter(key => key.startsWith('gaia-')).map(key => key.replace('gaia-', ''))
  ).size;
  const visitedDirectoryUids = new Set<string>();

  await Promise.all(
    directoryUids.map(directoryUid =>
      collectDirectoryFileUids(directoryUid, fileUids, visitedDirectoryUids)
    )
  );

  return fileUids.size + gaiaFileCount + selectedUploadCount;
}

async function queueAnalysisCostRefresh(): Promise<void> {
  if (isStartingAnalysis.value) return;

  costRequestSequence.value++;
  const requestSequence = costRequestSequence.value;
  const promptCollectionUid = selectedPromptCollection.value;
  const signature = currentCostSignature.value;
  const selectedKeys = [...selectedFileKeys.value];
  const selectedUploadCount = pendingUploadFilesCount.value;

  analysisCost.value = null;
  analysisCostError.value = null;
  analysisCostSignature.value = '';

  if (
    !props.show ||
    !promptCollectionUid ||
    selectedKeys.length + selectedUploadCount <= 0 ||
    !signature
  ) {
    analysisCostLoading.value = false;
    return;
  }

  analysisCostLoading.value = true;

  try {
    const numFiles = await getSelectedBillableFileCount(selectedKeys, selectedUploadCount);

    if (requestSequence !== costRequestSequence.value || signature !== currentCostSignature.value) {
      return;
    }

    if (numFiles <= 0) {
      analysisCostLoading.value = false;
      analysisCostError.value = 'No files were found in the current selection.';
      return;
    }

    void debouncedFetchAnalysisCost(promptCollectionUid, numFiles, signature, requestSequence);
  } catch (error) {
    if (requestSequence !== costRequestSequence.value) {
      return;
    }

    logger.error('Failed to count selected files:', error);
    analysisCost.value = null;
    analysisCostSignature.value = '';
    analysisCostLoading.value = false;
    analysisCostError.value = extractErrorDetail(
      error,
      'Failed to calculate analysis cost for the selected files.'
    );
  }
}

async function handleRunAnalysis(): Promise<void> {
  if (isStartingAnalysis.value || processingAnalysis.value) {
    return;
  }

  if (!authStore.userPermissions.can_create_reviews) {
    message.error('You do not have permission to create reviews.');
    return;
  }
  // Show validation errors
  showValidation.value = true;

  // Check validation and show specific error messages
  if (!analysisName.value) {
    message.error('Please enter an analysis name');
    return;
  }

  if (!selectedPromptCollection.value) {
    message.error('Please select a prompt collection');
    return;
  }

  // Check if we have files selected from vault OR files ready to upload
  const hasVaultFiles = selectedFileKeys.value.length > 0;
  const hasUploadFiles = (fileSelectorRef.value?.getSelectedFiles()?.length ?? 0) > 0;

  if (!hasVaultFiles && !hasUploadFiles) {
    message.error('Please select at least one file or directory');
    return;
  }

  if (!isCostReady.value) {
    message.error(runAnalysisDisabledReason.value || 'Please wait for the cost estimate to finish');
    return;
  }

  if (isAnalysisOverCreditLimit.value) {
    message.error('Not enough credits to run this analysis.');
    return;
  }

  isStartingAnalysis.value = true;
  updateStartProgress(5, 'Preparing analysis...');
  try {
    // First, upload the Upload tab's files into the folder picked there
    let uploadedFileUids: string[] = [];
    if (hasUploadFiles && fileSelectorRef.value) {
      updateStartProgress(15, 'Uploading selected files...');
      uploadedFileUids = await fileSelectorRef.value.uploadSelectedFiles();
    }
    updateStartProgress(35, 'Preparing selected files...');

    // Parse the selected keys to get UIDs from vault
    const selectedVaultFileUids = selectedFileKeys.value
      .filter(key => key.startsWith('file-'))
      .map(key => key.replace('file-', ''));
    const dirUids = selectedFileKeys.value
      .filter(key => key.startsWith('dir-'))
      .map(key => key.replace('dir-', ''));
    const gaiaUids = selectedFileKeys.value
      .filter(key => key.startsWith('gaia-'))
      .map(key => key.replace('gaia-', ''));

    // Import GAIA files to vault if any are selected
    const importedFileUids: string[] = [];
    if (gaiaUids.length > 0) {
      updateStartProgress(40, 'Importing GAIA files...');
      message.info(`Importing ${gaiaUids.length} GAIA file(s) to vault...`);

      for (let i = 0; i < gaiaUids.length; i++) {
        const gaiaFileId = gaiaUids[i];
        const gaiaFile = gaiaStore.getGaiaFileById(gaiaFileId);
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        const fileName = gaiaFile?.title || `File ${gaiaFileId}`;

        try {
          message.info(`Importing ${fileName} (${i + 1}/${gaiaUids.length})...`);
          const importedFile = await gaiaStore.importGaiaFileToVault(gaiaFileId);
          importedFileUids.push(importedFile.uid);
          updateStartProgress(
            40 + Math.round(((i + 1) / gaiaUids.length) * 15),
            'Importing GAIA files...'
          );
        } catch (error) {
          logger.error(`Failed to import GAIA file ${gaiaFileId}:`, error);
          const errorMsg = extractErrorDetail(error, 'Failed to start analysis. Please try again.');
          message.error(`Failed to import ${fileName}: ${errorMsg}`);
          return;
        }
      }

      message.success(`Successfully imported ${importedFileUids.length} GAIA file(s) to vault`);
      await vaultStore.getVaultDirectory();
    }

    const combinedFileUids = [
      ...new Set([...selectedVaultFileUids, ...uploadedFileUids, ...importedFileUids]),
    ];

    // Step 1: Create the evaluation
    updateStartProgress(60, 'Creating analysis...');
    const reviewPayload: ReviewCreateUser = {
      name: analysisName.value,
      description: null,
      ocr_provider: OcrProvider.UNSTRUCTURED,
      prompt_collection_uids: [selectedPromptCollection.value],
      prompt_uids: [],
    };

    const newReview = await reviewsStore.createReview(reviewPayload);

    if (newReview && typeof newReview === 'string') {
      // Step 2: Create file evaluations
      updateStartProgress(75, 'Starting file processing...');
      const fileReviewPayload = {
        vault_file_uids: combinedFileUids,
        vault_directory_uids: dirUids,
        ocr_provider: 'unstructured' as const,
      };

      try {
        const response = await createFileReviews({
          path: { review_uid: newReview },
          body: fileReviewPayload,
        });

        if (!response.data) {
          throw new Error('Failed to create file evaluation');
        }
        const fileReviews = response.data;
        message.success('Analysis started successfully');
        updateStartProgress(90, 'Refreshing analyses...');

        // Trigger confetti celebration (with time-based condition)
        if (props.triggerConfetti) {
          props.triggerConfetti();
        }

        const totalFiles = fileReviews.length || combinedFileUids.length + dirUids.length;

        // Refresh evaluations
        await reviewsStore.fetchReviews();
        updateStartProgress(96, 'Opening analysis...');

        // Redirect based on file count
        if (totalFiles === 1 && fileReviews.length > 0) {
          // Single file -> review-contract with fileReviewUid
          await router.push({
            path: `/review-contract/${newReview}`,
            query: {
              fileReviewUid: fileReviews[0].uid,
              source: 'detail',
            },
          });
        } else {
          // Multiple files -> review-table
          await router.push(`/review-table/${newReview}`);
        }

        // Emit the started evaluation
        emit('analysis-started', { uid: newReview });
        updateStartProgress(100, 'Analysis started.');
        showModal.value = false;
      } catch (fileEvaluationError) {
        logger.error('Failed to create file evaluations:', fileEvaluationError);
        const errorMsg = extractErrorDetail(
          fileEvaluationError,
          'Failed to start analysis. Please try again.'
        );
        message.error(`Analysis created but file processing failed: ${errorMsg}`);
        // Still close the modal since the evaluation was created

        await reviewsStore.fetchReviews();

        // Redirect even if file processing failed, since evaluation was created
        await router.push(`/review-table/${newReview}`);

        emit('analysis-started', { uid: newReview });
        showModal.value = false;
      }
    } else {
      // Handle case where evaluation creation returns the evaluation object directly
      message.success('Analysis started successfully');
      updateStartProgress(90, 'Refreshing analyses...');

      // Trigger confetti celebration (with time-based condition)
      if (props.triggerConfetti) {
        props.triggerConfetti();
      }

      await reviewsStore.fetchReviews();
      updateStartProgress(96, 'Opening analysis...');

      // Redirect to table view (we don't have fileReviewUid in this path)
      const reviewUid = typeof newReview === 'string' ? newReview : undefined;
      await router.push(`/review-table/${reviewUid}`);

      emit('analysis-started', { uid: reviewUid ?? '' });
      showModal.value = false;
    }
  } catch (error) {
    logger.error('Failed to create analysis:', error);
    const errorMsg = extractErrorDetail(error, 'Failed to start analysis. Please try again.');
    message.error(errorMsg);
  } finally {
    isStartingAnalysis.value = false;
  }
}

// Watch for modal close to reset form
watch(
  () => props.show,
  newShow => {
    if (newShow) {
      pendingUploadFilesCount.value = props.preUploadedFiles.length;
      void queueAnalysisCostRefresh();
    } else {
      handleModalClose();
    }
  }
);

watch(
  () => props.preUploadedFiles,
  newFiles => {
    pendingUploadFilesCount.value = newFiles.length;
    void queueAnalysisCostRefresh();
  },
  { immediate: true }
);

watch(
  [selectedPromptCollection, selectedFileKeys, pendingUploadFilesCount],
  () => {
    void queueAnalysisCostRefresh();
  },
  { deep: true }
);

// Lifecycle hooks
onMounted(async () => {
  try {
    await promptCollectionsStore.fetchCollections();
  } catch (error) {
    logger.error('Error fetching prompt collections:', error);
    message.error('Failed to load prompt collections');
  }
});
</script>

<template>
  <NModal
    v-model:show="showModal"
    preset="card"
    title="Run Document Analysis"
    :style="modalStyle"
    :mask-closable="!isStartingAnalysis"
    :closable="!isStartingAnalysis"
    @close="handleModalClose"
  >
    <div class="modal-content" :data-tab="fileSelectorActiveTab">
      <NForm>
        <div :class="fileSelectorActiveTab === 'upload' ? 'form-column' : 'form-row'">
          <NFormItem
            label="Analysis Name"
            :class="fileSelectorActiveTab === 'upload' ? 'form-item-full' : 'form-item-half'"
            :status="isAnalysisNameValid ? 'success' : 'error'"
          >
            <gaia-input
              v-model:value="analysisName"
              placeholder="Enter analysis name"
              :status="isAnalysisNameValid ? 'success' : 'error'"
            />
          </NFormItem>

          <NFormItem
            label="Prompt Collection"
            :class="fileSelectorActiveTab === 'upload' ? 'form-item-full' : 'form-item-half'"
            :status="isPromptCollectionValid ? 'success' : 'error'"
          >
            <gaia-select
              v-model:value="selectedPromptCollection"
              :options="promptCollectionOptions"
              placeholder="Select prompt collection"
              :status="isPromptCollectionValid ? 'success' : 'error'"
            />
          </NFormItem>
        </div>

        <FileSelector
          ref="fileSelectorRef"
          v-model:selected-keys="selectedFileKeys"
          :hide-columns="true"
          :pre-uploaded-files="props.preUploadedFiles"
          :show-file-vault-tab="!props.preUploadedFiles || props.preUploadedFiles.length === 0"
          @tab-change="fileSelectorActiveTab = $event"
          @files-uploaded="handleFilesUploaded"
          @upload-selection-change="handleUploadSelectionChange"
        />
      </NForm>
    </div>
    <template #footer>
      <div class="modal-footer">
        <NProgress
          v-if="isStartingAnalysis"
          type="line"
          :percentage="startAnalysisProgress"
          :processing="startAnalysisProgress < 100"
          :show-indicator="false"
        />
        <div v-if="isStartingAnalysis" class="progress-text">
          {{ startAnalysisStatus }}
        </div>

        <NAlert
          v-if="selectedPromptCollection || selectedInputCount > 0 || analysisCostError"
          :type="costNoticeType"
          :show-icon="false"
          class="cost-notice"
        >
          <div v-if="analysisCostLoading" class="cost-summary">
            <strong>Calculating analysis cost...</strong>
          </div>
          <div v-else-if="analysisCostError" class="cost-summary">
            <strong>Cost unavailable</strong>
            <span>{{ analysisCostError }}</span>
          </div>
          <div v-else-if="isCostReady && analysisCost" class="cost-summary">
            <span>
              Estimated cost:
              <strong>{{ formatCredits(analysisCost.total_cost) }} credits</strong>
            </span>
            <span>
              {{ analysisCost.num_files }} file{{ analysisCost.num_files === 1 ? '' : 's' }},
              {{ analysisCost.num_prompts }} prompt{{ analysisCost.num_prompts === 1 ? '' : 's' }}
            </span>
            <span v-if="isAnalysisOverCreditLimit" class="credit-warning">
              Not enough credits. You need {{ formatCredits(analysisCreditDeficit) }} more credit{{
                analysisCreditDeficit === 1 ? '' : 's'
              }}. Please contact GAIA Support.
            </span>
            <span v-else>
              Remaining after run:
              <strong>{{ formatCredits(remainingCreditsAfterRun) }}</strong>
              of {{ formatCredits(analysisCost.allowed_org_credits) }} credits
            </span>
          </div>
          <div v-else class="cost-summary">
            <strong>Cost estimate pending</strong>
            <span>Select files and a prompt collection to calculate the analysis cost.</span>
          </div>
        </NAlert>

        <div class="footer-actions-row">
          <div v-if="uploadedFilesCount > 0" class="uploaded-files-message">
            {{ uploadedFilesCount }} file{{ uploadedFilesCount > 1 ? 's' : '' }} uploaded
            successfully
          </div>
          <div v-else-if="runAnalysisDisabledReason" class="disabled-reason">
            {{ runAnalysisDisabledReason }}
          </div>
          <div v-else></div>
          <NSpace>
            <NButton :disabled="isStartingAnalysis" @click="handleCancel"> Cancel </NButton>
            <NButton
              type="primary"
              :disabled="!canRunAnalysis"
              :loading="isStartingAnalysis || processingAnalysis"
              @click="handleRunAnalysis"
            >
              Run Analysis
            </NButton>
          </NSpace>
        </div>
      </div>
    </template>
  </NModal>
</template>

<style scoped>
/* Modal form styles */
.modal-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(100vh - 120px);
  overflow: hidden;
}

/* Allow scrolling for specific tabs */
.modal-content[data-tab='file-vault'],
.modal-content[data-tab='gaia-sections'] {
  overflow: visible;
}

/* Ensure proper scrolling for tabs */
.modal-content :deep(.n-tabs) {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.modal-content :deep(.n-tabs-content) {
  flex: 1;
  overflow: hidden;
}

.modal-content :deep(.n-tab-pane) {
  height: 100%;
  overflow: hidden;
}

/* Allow scrolling for specific tab panes */
.modal-content :deep(.n-tab-pane[data-name='file-vault']) {
  overflow: visible;
}

.modal-content :deep(.n-tab-pane[data-name='gaia-sections']) {
  overflow: visible;
}

/* When in file-vault or gaia-sections tabs, allow the tabs content to scroll */
.modal-content[data-tab='file-vault'] :deep(.n-tabs-content),
.modal-content[data-tab='gaia-sections'] :deep(.n-tabs-content) {
  overflow: visible;
}

.modal-content > .n-form {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 12px;
}

.form-item-half {
  flex: 1;
  min-width: 0;
}

.form-item-full {
  width: 100%;
}

.form-item-half :deep(.n-form-item-label) {
  justify-content: flex-start;
}

.modal-footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.cost-notice {
  width: 100%;
}

.cost-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  align-items: center;
  font-size: 13px;
}

.credit-warning {
  font-weight: 600;
}

.footer-actions-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.uploaded-files-message {
  color: var(--n-color-success);
  font-size: 14px;
  font-weight: 500;
}

.disabled-reason {
  color: var(--n-text-color-3);
  font-size: 13px;
}

.progress-text {
  color: var(--n-text-color-3);
  font-size: 13px;
}
</style>
